import React, { useEffect, useState, useMemo, useRef } from "react";
import { Modal, FormActionButtons } from "@/components/ui";
import { useUsers } from "@/hooks/useUsers";
import { useContactForm } from "../hooks/useContactForm";
import { SpaceInput } from "../../../components/ui/Input";
import { SpaceButton } from "../../../components/ui/Button";
import { useAuthStore } from "@/store/authStore";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Controller } from "react-hook-form";
import CreatableSelect from 'react-select/creatable';
import { INDIAN_STATES } from "@/constants/globalConstants";
import { useContacts } from "../hooks/useContacts";

const CITIES_BY_STATE = {
  "Andhra Pradesh": ["Vijayawada", "Visakhapatnam", "Guntur", "Nellore", "Tirupati"],
  "Telangana": ["Hyderabad", "Warangal", "Nizamabad", "Khammam", "Karimnagar"],
  "Karnataka": ["Bengaluru", "Mysore", "Hubli", "Mangalore", "Belgaum"],
  "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai", "Trichy", "Salem"]
};

export const CustomerFormModal = ({
  isOpen,
  onClose,
  initialData,
  mode = "add",
  currentUser = null
}) => {
  const { user: authUser } = useAuthStore();
  const activeUser = currentUser || authUser;
  const { teamMembers } = useUsers();
  const navigate = useNavigate();
  const { createContact, updateContact } = useContacts();

  const currentUserData = JSON.parse(localStorage.getItem('company_user') || '{}');
  const storeRef = currentUserData.storeRef;
  const storeName = currentUserData.storeName || currentUserData.store?.storeName || currentUserData.store_name || storeRef;

  const handleSuccess = (result, shouldNavigate = false) => {
    onClose();
    if (shouldNavigate) {
      const targetId = result?._id || result?.id || '1';
      navigate(`/workspace/${targetId}`, { state: { customer: result, showForm: true } });
    } else {
      toast.success("Successfully saved");
    }
  };

  const {
    form,
    resetForm
  } = useContactForm(initialData, handleSuccess);

  const { register, formState: { errors }, watch, control } = form;

  const watchedState = watch("state");
  const watchedReferenceType = watch("referenceType");
  const watchedCustomerType = watch("customerType");

  const teams = useMemo(() => {
    const depts = teamMembers
      .map(m => m.department)
      .filter(d => d && d.trim() !== "");
    const defaults = ["Sales Team", "Marketing Team", "Dealer Support", "Corporate Sales", "Hyderabad Sales", "Installation Team"];
    return Array.from(new Set([...depts, ...defaults]));
  }, [teamMembers]);

  const cities = useMemo(() => {
    return watchedState ? (CITIES_BY_STATE[watchedState] || []) : ["Vijayawada", "Hyderabad", "Bengaluru", "Chennai"];
  }, [watchedState]);

  useEffect(() => {
    if (isOpen && mode === "add") {
      resetForm();
      if (activeUser?._id) {
        form.setValue("assignedTo", activeUser._id);
      }
      form.setValue("referenceType", "");
      form.setValue("customerType", "");
      form.setValue("category", "Enquiry");
    }
  }, [isOpen, mode, resetForm, activeUser, form]);



  const handleActualSubmit = async (data, shouldNavigate = false) => {
    try {
      if (mode === 'add' && (!storeRef || storeRef === 'all')) {
        toast.error("You cannot add a customer in 'All Stores'. Please select a specific store first.");
        return;
      }

      let result;

      const finalCustomerType = data.customerType === 'Others' ? data.customerTypeOther : (data.salesType || data.customerType);
      const finalLeadSource = data.referenceType === 'Others' ? data.referenceTypeOther : (data.referenceType || data.leadSource);

      if (mode === 'edit' && initialData) {
        result = await updateContact({ 
          id: initialData._id, 
          data: {
            ...data,
            customerName: data.firstName || data.customerName,
            phone: data.phoneNumber || data.phone,
            customerType: finalCustomerType,
            leadSource: finalLeadSource,
            pinCode: data.pin_code || data.pincode || data.pinCode,
            addressLine1: data.address_line_1 || data.addressLine1,
            addressLine2: data.address_line_2 || data.addressLine2,
            referredBy: data.referred_by || data.referredBy,
            interestedIn: data.notes || data.interestedIn
          } 
        });
      } else {
        result = await createContact({
          ...data,
          customerName: data.firstName || data.customerName,
          phone: data.phoneNumber || data.phone,
          customerType: finalCustomerType,
          leadSource: finalLeadSource,
          pinCode: data.pin_code || data.pincode || data.pinCode,
          addressLine1: data.address_line_1 || data.addressLine1,
          addressLine2: data.address_line_2 || data.addressLine2,
          referredBy: data.referred_by || data.referredBy,
          interestedIn: data.notes || data.interestedIn,
          status: "new",
          store: storeRef
        });
      }
      
      handleSuccess(result, shouldNavigate);
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error?.response?.data?.message || "Error saving customer");
    }
  };

  const handleSaveAsLead = form.handleSubmit((data) => handleActualSubmit(data, false));
  const handleSaveAndStart = form.handleSubmit((data) => handleActualSubmit(data, true));

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="max-w-xl"
      title={
        <div className="flex flex-col gap-0.5 z-10 relative">
          <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
            SPACE DESIGNS
          </span>
          <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
            {mode === 'view' ? "Customer Details" : mode === 'edit' ? "Edit Enquiry" : "New Walk-in / Enquiry"}
          </span>
          {(mode === 'add' || mode === 'edit') && (
            <span className="text-[10px] text-slate-100 mt-1">
              Store: <span className={(!storeRef || storeRef === 'all') ? 'text-red-400 font-bold' : 'font-medium'}>
                {(!storeRef || storeRef === 'all') ? "All Stores (Select specific store to add)" : storeName}
              </span>
            </span>
          )}
        </div>
      }
    >
      <form onSubmit={form.handleSubmit(handleActualSubmit)} className="flex flex-col gap-6 p-2">
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2 col-span-1">
            <label className="text-xs font-medium text-gray-500">Category *</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                <input 
                  type="radio" 
                  value="Enquiry" 
                  disabled={mode === 'view'}
                  className="w-4 h-4 text-slate-900 border-gray-300 focus:ring-slate-900"
                  {...register("category")}
                />
                Enquiry
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                <input 
                  type="radio" 
                  value="Others" 
                  disabled={mode === 'view'}
                  className="w-4 h-4 text-slate-900 border-gray-300 focus:ring-slate-900"
                  {...register("category")}
                />
                Others
              </label>
            </div>
            {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category.message}</p>}
          </div>

          <div className="flex flex-col gap-1.5 col-span-1">
            <SpaceInput 
              label="Customer type" 
              type="select" 
              disabled={mode === 'view'}
              {...register("customerType")}
            >
              <option value="">Select Customer Type</option>
              <option value="Retail">Retail</option>
              <option value="Dealer">Dealer</option>
              <option value="Corporate">Corporate</option>
              <option value="Others">Others</option>
            </SpaceInput>
            {watchedCustomerType === 'Others' && (
              <SpaceInput 
                placeholder="Specify Customer Type" 
                disabled={mode === 'view'}
                {...register("customerTypeOther", { required: "Please specify the custom customer type" })}
                error={errors.customerTypeOther?.message}
                className="mt-1"
              />
            )}
          </div>

          <SpaceInput 
            label="Name *" 
            placeholder="Enter customer name" 
            className="col-span-1"
            disabled={mode === 'view'}
            {...register("firstName")}
            error={errors.firstName?.message}
            required
          />
          
          <SpaceInput 
            label="Phone" 
            placeholder="XXXXXXXXXX" 
            className="col-span-1"
            disabled={mode === 'view'}
            maxLength={10}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/\D/g, '');
            }}
            {...register("phoneNumber", {
              pattern: {
                value: /^\d{10}$/,
                message: "Phone number must be exactly 10 digits"
              }
            })}
            error={errors.phoneNumber?.message}
          />

          <SpaceInput 
            label="Address Line 1" 
            placeholder="House No, Street, Area" 
            className="col-span-1"
            disabled={mode === 'view'}
            {...register("address_line_1")}
            error={errors.address_line_1?.message}
          />

          <SpaceInput 
            label="Address Line 2" 
            placeholder="Apartment, Landmark (Optional)" 
            className="col-span-1"
            disabled={mode === 'view'}
            {...register("address_line_2")}
            error={errors.address_line_2?.message}
          />

          <div className="flex flex-col gap-1.5">
            <SpaceInput 
              label="State" 
              placeholder="Select or enter state"
              list="states-list"
              disabled={mode === 'view'}
              {...register("state")}
              error={errors.state?.message}
            />
            <datalist id="states-list">
              {INDIAN_STATES.map(s => <option key={s} value={s} />)}
            </datalist>
          </div>

          <div className="flex flex-col gap-1.5">
            <SpaceInput 
              label="City *" 
              placeholder="Select or enter city"
              list="cities-list"
              disabled={mode === 'view'}
              {...register("city", { required: "City is required" })}
              error={errors.city?.message}
            />
            <datalist id="cities-list">
              {cities.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>

          <SpaceInput 
            label="PIN Code" 
            placeholder="6-digit PIN"
            maxLength={6}
            className="col-span-1"
            disabled={mode === 'view'}
            {...register("pin_code")}
            error={errors.pin_code?.message}
          />
          
          <div className="flex flex-col gap-1.5 col-span-1">
            <SpaceInput 
              label="Lead source" 
              type="select" 
              disabled={mode === 'view'}
              {...register("referenceType")}
            >
              <option value="">Select Lead Source</option>
              <option value="Walk-in">Walk-in</option>
              <option value="Referral">Referral</option>
              <option value="Website">Website</option>
              <option value="Others">Others</option>
            </SpaceInput>
            {watchedReferenceType === 'Others' && (
              <SpaceInput 
                placeholder="Specify Lead Source" 
                disabled={mode === 'view'}
                {...register("referenceTypeOther", { required: "Please specify the custom lead source" })}
                error={errors.referenceTypeOther?.message}
                className="mt-1"
              />
            )}
          </div>

          {watchedReferenceType === "Referral" && (
            <SpaceInput 
              label="Referred By *" 
              placeholder="Enter referrer name" 
              className="col-span-2"
              disabled={mode === 'view'}
              {...register("referred_by")}
              error={errors.referred_by?.message}
              required
            />
          )}
          
          <SpaceInput 
            label="Assign to" 
            type="select" 
            className="col-span-2"
            disabled={mode === 'view'}
            {...register("assignedTo")}
          >
            <option value="">Select a member</option>
            {teamMembers.map((m) => {
              const name = m.name || m.firstName || m.username || 'Unknown';
              const role = m.role || m.department || m.designation || 'Staff';
              let loc = '';
              if (m.workLocation && m.workLocation.trim() !== '') {
                loc = ` (${m.workLocation.substring(0, 3).toUpperCase()})`;
              }
              return (
                <option key={m._id || Math.random()} value={m._id}>
                  {name} - {role}{loc}
                </option>
              );
            })}
          </SpaceInput>
          
          <div className="flex flex-col gap-1.5 col-span-2">
            <label className="text-xs font-medium text-gray-500">Interested in</label>
            <textarea
              rows={4}
              placeholder="Describe customer requirements, products interested in, measurements, project details, preferred colors, quantity, site information, etc."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 disabled:bg-slate-50 disabled:text-slate-500"
              disabled={mode === 'view'}
              {...register("notes")}
            />
            {errors.notes && <p className="text-xs text-red-500 mt-1">{errors.notes.message}</p>}
          </div>

        </div>

        <FormActionButtons 
          onCancel={mode === 'view' ? onClose : handleSaveAsLead}
          cancelText={mode === 'view' ? "Close" : "Save as lead"}
          onSubmit={handleSaveAndStart}
          submitText="Save & Start Estimation ➔"
          hideSubmit={mode === 'view'}
        />
      </form>
    </Modal>
  );
};